import clsx from "clsx";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { Link, useLocation } from "react-router-dom";
import TreeItem from "@mui/lab/TreeItem";
import { Box, Icon, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";

import { ReactComponent as WorkspaceIconSVG } from "../../../assets/img/WorkspaceIconSmallFillToCurrentColor.svg";

const HIDE_ATTENTION_INDICATOR = true;  // Hide the attention indicator. Remove when fully implemented.

const useStyles = makeStyles(theme => ({
  content: {
    cursor: "default",
    marginBottom: 2,
    minHeight: 36,
    padding: 0,
    width: "auto",
    borderRadius: 8,
    transition:
      "background-color 140ms ease, box-shadow 140ms ease, color 140ms ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.text.primary, 0.035),
    },
  },

  activeContent: {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.085)} !important`,
    boxShadow: `inset 2px 0 0 ${theme.palette.primary.main}`,
    "& $labelRoot": {
      color: theme.palette.primary.main,
    },
    "& $labelIcon": {
      color: theme.palette.primary.main,
    },
    "& $label": {
      fontWeight: 560,
    },
    "&:hover": {
      backgroundColor: `${alpha(theme.palette.primary.main, 0.115)} !important`,
    },
  },

  parentOfActiveContent: {
    backgroundColor: alpha(theme.palette.primary.main, 0.025),
    "& $labelRoot": {
      color: alpha(theme.palette.text.primary, 0.70),
    },
    "& $labelIcon": {
      color: theme.palette.primary.main,
    },
    "& $label": {
      fontWeight: 550,
    },
  },

  selected: {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.065)} !important`,
    "&:hover": {
      backgroundColor: `${alpha(theme.palette.primary.main, 0.095)} !important`,
    },
  },

  labelRoot: {
    alignItems: "center",
    color: alpha(theme.palette.text.primary, 0.78),
    cursor: "pointer",
    display: "flex",
    minHeight: 36,
    minWidth: 0,
    padding: "0 4px 0 3px",
    textDecoration: "none",
    width: "100%",
    borderRadius: 8,
    transition: "color 140ms ease",
    "&:active,&:focus,&:hover": {
      color: theme.palette.text.primary,
      textDecoration: "none",
    },
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.primary.main, 0.22)}`,
      outlineOffset: -2,
    },
  },

  labelDisabledNode: {
    alignItems: "center",
    color: alpha(theme.palette.text.primary, 0.34),
    cursor: "default",
    display: "flex",
    minHeight: 36,
    minWidth: 0,
    padding: "0 4px 0 3px",
    textDecoration: "none",
    width: "100%",
  },

  labelIcon: {
    color: alpha(theme.palette.text.primary, 0.58),
    fontSize: 15,
    marginLeft: 0,
    marginRight: 6,
    minWidth: 18,
    overflow: "unset",
    textAlign: "center",
    transition: "color 140ms ease",
  },

  label: {
    flexGrow: 1,
    fontSize: 13.7,
    fontWeight: 470,
    letterSpacing: "-0.006em",
    lineHeight: 1.2,
    minWidth: 0,
  },

  statusSlot: {
    alignItems: "center",
    display: "flex",
    flex: "0 0 30px",
    height: 20,
    justifyContent: "flex-end",
    marginLeft: 5,
    minWidth: 30,
    paddingRight: 1,
  },

  svgIcon: {
    height: "1.04em",
    "& > svg": {
      height: "1.04em",
    },
  },
}));

const DEFAULT_ATTENTION = Object.freeze({
  requiresAction: false,
  severity: "warning",
});

const normalizeAttention = attention => {
  if (attention === true) {
    return { ...DEFAULT_ATTENTION, requiresAction: true };
  }

  if (!attention || attention === false) {
    return DEFAULT_ATTENTION;
  }

  return {
    ...DEFAULT_ATTENTION,
    ...attention,
    requiresAction: Boolean(attention.requiresAction),
  };
};

const AdminSidebarTreeItem = ({
  attentionStatus,
  childtree,
  enabledByRights,
  icon,
  idx,
  label,
  link,
  usersRights,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const location = useLocation();

  const checkEnabled = validRights =>
    (validRights ?? []).some(x => (usersRights ?? []).includes(x));

  const rootItem = {
    enabledByRights,
    icon,
    label,
    link,
  };

  const normalizePath = value => {
    if (!value) return "/";
    const normalized = value.replace(/\/+$/, "");
    return normalized || "/";
  };

  const isItemActive = item => {
    if (!item?.link) return false;

    const currentPath = normalizePath(location.pathname);
    const itemPath = normalizePath(item.link);

    return (
      currentPath === itemPath ||
      (itemPath !== "/" && currentPath.startsWith(`${itemPath}/`))
    );
  };

  const hasActiveDescendant = item =>
    Array.isArray(item?.childtree) &&
    item.childtree.some(child => isItemActive(child));

  const getOwnAttention = item => {
    if (!item?.attentionKey) return DEFAULT_ATTENTION;
    return normalizeAttention(attentionStatus?.[item.attentionKey]);
  };

  const getActionCount = item => {
    const ownCount = getOwnAttention(item).requiresAction ? 1 : 0;

    if (!Array.isArray(item?.childtree) || item.childtree.length === 0) {
      return ownCount;
    }

    return (
      ownCount +
      item.childtree.reduce((sum, child) => sum + getActionCount(child), 0)
    );
  };

  const getLeafTooltip = item => {
    const attention = getOwnAttention(item);

    if (attention.message) {
      return attention.message;
    }

    if (attention.messageId) {
      return intl.formatMessage({
        id: attention.messageId,
        defaultMessage: "Åtgärd krävs",
      });
    }

    return intl.formatMessage({
      id: "admin.sidebar.attention.required",
      defaultMessage: "Åtgärd krävs",
    });
  };

  const getAggregateTooltip = count => {
    if (count === 1) {
      return intl.formatMessage({
        id: "admin.sidebar.attention.child",
        defaultMessage: "1 undersida behöver åtgärdas.",
      });
    }

    return intl.formatMessage(
      {
        id: "admin.sidebar.attention.multiple",
        defaultMessage: "{count} undersidor behöver åtgärdas.",
      },
      { count }
    );
  };

  const AttentionIndicator = ({ item }) => {
    const ownAttention = getOwnAttention(item);
    const hasChildren =
      Array.isArray(item?.childtree) && item.childtree.length > 0;
    const aggregateCount = getActionCount(item);

    if (aggregateCount === 0) {
      return <Box component="span" className={classes.statusSlot} />;
    }

    if (hasChildren) {
      const tooltip = getAggregateTooltip(aggregateCount);

      return (
        <Box component="span" className={classes.statusSlot}>
          <Tooltip arrow placement="right" title={tooltip}>
            <Box
              component="span"
              aria-label={tooltip}
              sx={{
                minWidth: 20,
                height: 18,
                px: 0.55,
                borderRadius: 9,
                border: theme =>
                  `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                bgcolor: theme => alpha(theme.palette.primary.main, 0.075),
                color: "primary.main",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10.5,
                fontWeight: 800,
                lineHeight: 1,
                letterSpacing: "-0.01em",
                boxSizing: "border-box",
              }}
            >
              {aggregateCount}
            </Box>
          </Tooltip>
        </Box>
      );
    }

    if (!ownAttention.requiresAction) {
      return <Box component="span" className={classes.statusSlot} />;
    }

    const tooltip = getLeafTooltip(item);
    const isError = ownAttention.severity === "error";

    return (
      <Box component="span" className={classes.statusSlot}>
        <Tooltip arrow placement="right" title={tooltip}>
          <Box
            component="span"
            aria-label={tooltip}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: isError ? "#D92D20" : "#D99000",
              boxShadow: isError
                ? "0 0 0 2px rgba(217,45,32,.11)"
                : "0 0 0 2px rgba(217,144,0,.12)",
              flexShrink: 0,
              mr: "5px",
            }}
          />
        </Tooltip>
      </Box>
    );
  };

  const ItemIcon = ({ item }) => {
    switch (item.icon) {
      case "fas fa-layer-group":
        return (
          <span className={clsx([classes.svgIcon, classes.labelIcon, "fas"])}>
            <WorkspaceIconSVG />
          </span>
        );

      case "fas fa-question":
        return (
          <span
            className="fa-stack fa-2x"
            style={{
              fontSize: "0.58em",
              marginLeft: "0px",
              marginRight: "6px",
              minWidth: "18px",
            }}
          >
            <span
              className="fas fa-file fa-stack-2x"
              style={{ verticalAlign: "middle" }}
            />
            <span
              className="fas fa-question fa-stack-1x fa-inverse"
              style={{
                verticalAlign: "middle",
                marginLeft: "-0.7px",
                marginTop: "1.4px",
              }}
            />
          </span>
        );

      case "fas fa-exclamation":
        return (
          <span
            className="fa-stack fa-2x"
            style={{
              fontSize: "0.58em",
              marginLeft: "0px",
              marginRight: "6px",
              minWidth: "18px",
            }}
          >
            <span
              className="fas fa-file fa-stack-2x"
              style={{ verticalAlign: "middle" }}
            />
            <span
              className="fas fa-exclamation fa-stack-1x fa-inverse"
              style={{
                verticalAlign: "middle",
                marginLeft: "-0.7px",
                marginTop: "1.4px",
              }}
            />
          </span>
        );

      default:
        return (
          <Icon className={clsx([`${item.icon}`, classes.labelIcon])} />
        );
    }
  };

  const IconWithLabel = ({ item }) => (
    <>
      <ItemIcon item={item} />
      <Typography noWrap className={classes.label}>
        {intl.formatMessage({ id: item.label })}
      </Typography>
      {!HIDE_ATTENTION_INDICATOR && <AttentionIndicator item={item} />}
    </>
  );

  const createLabel = item => {
    const isEnabled = checkEnabled(item.enabledByRights);

    return (
      <>
        {isEnabled && !item.link && (
          <Box className={classes.labelRoot}>
            <IconWithLabel item={item} />
          </Box>
        )}

        {isEnabled && item.link && (
          <Link to={item.link} className={classes.labelRoot}>
            <IconWithLabel item={item} />
          </Link>
        )}

        {!isEnabled && (
          <Box className={classes.labelDisabledNode}>
            <IconWithLabel item={item} />
          </Box>
        )}
      </>
    );
  };

  const getTreeItemClasses = item => ({
    content: clsx(
      classes.content,
      isItemActive(item) && classes.activeContent,
      !isItemActive(item) &&
        hasActiveDescendant(item) &&
        classes.parentOfActiveContent
    ),
    label: classes.label,
    root: classes.item,
    selected: classes.selected,
  });

  const parentItem = { ...rootItem, childtree };

  return (
    <>
      {childtree.length > 0 ? (
        <TreeItem
          classes={getTreeItemClasses(parentItem)}
          label={createLabel(parentItem)}
          nodeId={`parent-${idx}`}
        >
          {childtree.map((child, childIdx) => (
            <TreeItem
              classes={getTreeItemClasses(child)}
              key={`tree-item-${idx}-${childIdx}`}
              label={createLabel(child)}
              nodeId={`child-${idx}-${childIdx}`}
            />
          ))}
        </TreeItem>
      ) : (
        <TreeItem
          classes={getTreeItemClasses(rootItem)}
          label={createLabel(rootItem)}
          nodeId={`${idx}`}
        />
      )}
    </>
  );
};

AdminSidebarTreeItem.defaultProps = {
  attentionStatus: {},
  usersRights: [],
};

AdminSidebarTreeItem.propTypes = {
  attentionStatus: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        requiresAction: PropTypes.bool,
        severity: PropTypes.oneOf(["warning", "error"]),
        message: PropTypes.string,
        messageId: PropTypes.string,
      }),
    ])
  ),
  childtree: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  enabledByRights: PropTypes.arrayOf(PropTypes.string).isRequired,
  icon: PropTypes.string.isRequired,
  idx: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  usersRights: PropTypes.arrayOf(PropTypes.string),
};

AdminSidebarTreeItem.displayName = "AdminSidebarTreeItem";

export default AdminSidebarTreeItem;
